<template>
    <Head :title="`Activity — ${collaborator?.name}`" />

    <TailwindLayout :title="`Activity — ${collaborator?.name}`">
        <div class="bg-white rounded-lg shadow-sm dark:bg-zinc-800">

            <!-- Page header -->
            <div class="px-6 pt-6 pb-4 border-b border-gray-200 dark:border-zinc-700">
                <Link
                    :href="route('collaborators.activity.index')"
                    class="inline-flex items-center gap-1.5 text-sm font-semibold text-rent-risk-blue-600 hover:text-rent-risk-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                >
                    <font-awesome-icon :icon="faChevronLeft" class="h-3 w-3" aria-hidden="true" />
                    Access activity
                </Link>

                <div class="mt-4 flex items-center gap-4">
                    <div
                        class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-semibold text-white select-none"
                        :style="{ backgroundColor: avatarColor(collaborator?.name) }"
                        aria-hidden="true"
                    >
                        {{ initials(collaborator?.name) }}
                    </div>
                    <div>
                        <h1 class="text-3xl font-semibold leading-tight tracking-tight text-gray-900 dark:text-white">
                            Activity log
                        </h1>
                        <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                            {{ collaborator?.name }}
                            <span class="text-gray-300 dark:text-zinc-600 mx-1">·</span>
                            {{ collaborator?.email }}
                        </p>
                    </div>
                </div>
            </div>

            <div class="px-6 py-6">

                <!-- Events table -->
                <section class="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700">
                    <template v-if="events.data.length">
                        <table class="min-w-full divide-y divide-gray-200 dark:divide-zinc-700">
                            <thead class="bg-gray-50 dark:bg-zinc-700/50">
                                <tr>
                                    <th scope="col" class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                                        Time
                                    </th>
                                    <th scope="col" class="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                                        Event
                                    </th>
                                    <th scope="col" class="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 lg:table-cell">
                                        Property
                                    </th>
                                    <th scope="col" class="hidden px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-700 dark:text-gray-300 md:table-cell">
                                        Details
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-200 bg-white dark:divide-zinc-700 dark:bg-zinc-800">
                                <tr
                                    v-for="ev in events.data"
                                    :key="ev.id"
                                    class="hover:bg-gray-50 dark:hover:bg-zinc-700/40 transition-colors"
                                >
                                    <td class="whitespace-nowrap px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                                        {{ ev.created_at_display }}
                                    </td>
                                    <td class="px-4 py-3">
                                        <span class="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                                            <font-awesome-icon :icon="faEye" class="h-3 w-3" aria-hidden="true" />
                                            {{ formatEventType(ev.event_type) }}
                                        </span>
                                    </td>
                                    <td class="hidden px-4 py-3 text-sm text-gray-700 dark:text-gray-300 lg:table-cell">
                                        {{ ev.property?.label ?? '—' }}
                                    </td>
                                    <td class="hidden px-4 py-3 text-sm text-gray-600 dark:text-gray-400 md:table-cell">
                                        <span v-if="ev.event_type === 'application.viewed' && ev.metadata?.viewer_name">
                                            {{ ev.metadata.viewer_name }}
                                            <span v-if="ev.metadata?.viewer_email" class="ml-1 text-gray-400 dark:text-zinc-500">({{ ev.metadata.viewer_email }})</span>
                                        </span>
                                        <span v-else-if="ev.metadata?.tab">
                                            Tab: {{ tabLabel(ev.metadata.tab) }}
                                        </span>
                                        <span v-else class="text-gray-400 dark:text-zinc-600">—</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <Pagination class="border-t border-gray-100 dark:border-zinc-700 px-4" :links="events.links" :results="events" type="events" />
                    </template>

                    <!-- Empty state inside table card -->
                    <div v-else class="px-4 py-12 text-center">
                        <font-awesome-icon :icon="faClockRotateLeft" class="mx-auto h-8 w-8 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                        <p class="mt-3 text-sm font-medium text-gray-700 dark:text-gray-300">No recorded visits yet</p>
                        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            No activity has been recorded for this collaborator.
                        </p>
                    </div>
                </section>

            </div>
        </div>
    </TailwindLayout>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faEye, faClockRotateLeft } from '@fortawesome/pro-solid-svg-icons'
import { faScreenUsers, faChevronLeft } from '@fortawesome/pro-light-svg-icons'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import Pagination from '@/Shared/Pagination.vue'

defineProps({
    collaborator: {
        type: Object,
        required: true,
    },
    events: {
        type: Object,
        required: true,
    },
})

function initials(name) {
    if (!name) { return '?' }
    return name
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
}

const AVATAR_COLORS = [
    '#4F6D9F', '#2A7B5E', '#7B4F9A', '#9A6B2A',
    '#2A5F7B', '#7B2A2A', '#2A7B4F', '#5F4F7B',
]

function avatarColor(name) {
    if (!name) { return AVATAR_COLORS[0] }
    const code = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return AVATAR_COLORS[code % AVATAR_COLORS.length]
}

function formatEventType(type) {
    if (type === 'property.viewed') {
        return 'Property viewed'
    }
    if (type === 'application.viewed') {
        return 'Application viewed'
    }
    if (type === 'rent_payments.monthly_ledger_viewed') {
        return 'Monthly rental ledger viewed'
    }
    if (type === 'rent_payments.property_transactions_viewed') {
        return 'Property transactions viewed'
    }
    if (type === 'rent_payments.lease_history_viewed') {
        return 'Lease history viewed'
    }
    return type
}

function tabLabel(slug) {
    const map = {
        'tenant-screening': 'Tenant screening',
        'lease-agreement': 'Lease agreement',
        'rent-payments': 'Rent payments',
        maintenance: 'Maintenance',
        refinance: 'Refinance',
        'landlord-insurance': 'Landlord insurance',
    }
    return map[slug] ?? slug
}
</script>
